import { memo } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import { DateTime } from "luxon"
import { CreditNote } from "@oysterhr/schema"
import { Button, Heading, Link, Text } from "@oysterhr/ui"
import { CreditNoteKindLabelsMap } from "../../../constants/creditNotes"
import { CreditNoteStatusPill } from "../../CreditNotesTable/CreditNoteStatusPill"
import { Card, Row } from "../../Viewer"
import { staffInvoicesPath } from "../../../utils/routeHelpers"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { formatCreditNotePayrollMonth } from "../../../utils/formatCreditNotePayrollMonth"
import { SimpleMoney } from "@oysterhr/common"
import { companyInvoicesPath } from "@oysterhr/router"

interface Props {
  creditNote: CreditNote
}

export const CreditNoteDrawer = memo(({ creditNote }: Props) => {
  const { t } = useTranslation("CreditNotes")
  const { isStaff } = useAccountAspect()

  const kindLabel = CreditNoteKindLabelsMap[creditNote.kind]
  const history =
    creditNote.creditNoteApplications?.collection.map(
      ({ affectedInvoice, amountUsed, createdAt }) => ({
        date: createdAt,
        amount: amountUsed,
        referenceCode: affectedInvoice.referenceCode,
      }),
    ) ?? []

  return (
    <div className="flex flex-grow flex-col px-10 pb-20">
      {creditNote.file?.url && (
        <Button as={ReactRouterLink} to={creditNote.file.url} isFullWidth>
          {t("drawer.downloadDocument")}
        </Button>
      )}
      <div className="mt-8 flex flex-col gap-4">
        <Card>
          <div className="flex flex-col gap-2">
            <Heading size="xl">{creditNote.referenceCode}</Heading>
            <div className="flex items-center justify-between">
              <Text size="medium" className="font-medium">
                {t(kindLabel.translationKey)}
              </Text>
              <CreditNoteStatusPill creditNote={creditNote} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {isStaff && <Row label={t("drawer.customer")}>{creditNote.company.name}</Row>}
            <Row label={t("drawer.payrollMonth")}>{formatCreditNotePayrollMonth(creditNote)}</Row>
            <Row label={t("drawer.issueDate")}>
              {DateTime.fromISO(creditNote.issueDate).toLocaleString(DateTime.DATE_MED)}
            </Row>
            <Row label={t("drawer.amount")}>
              {SimpleMoney.fromNullable(creditNote.amount)?.format()}
            </Row>
          </div>
        </Card>
        <Card>
          <Text className="font-medium">{t("drawer.details")}</Text>
          <Row label={t("drawer.amountRemaining")}>
            <Heading size="2xl">
              {SimpleMoney.fromNullable(creditNote.availableAmount || creditNote.amount)?.format()}
            </Heading>
          </Row>
          {history.length > 0 && (
            <div className="flex flex-col gap-2">
              {history.map(({ date, amount, referenceCode }) => (
                <Row
                  label={DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED)}
                  key={referenceCode}
                >
                  <Text size="medium" className="text-gray-400">
                    <Trans
                      i18nKey="drawer.amountAppliedTo"
                      t={t}
                      values={{
                        amount: SimpleMoney.fromNullable(amount)?.format(),
                        referenceCode,
                      }}
                      components={{
                        a: (
                          <Link
                            className="inline"
                            target="_blank"
                            to={
                              isStaff
                                ? staffInvoicesPath(`?query=${referenceCode}`)
                                : companyInvoicesPath(
                                    creditNote.company.id,
                                    `?query=${referenceCode}`,
                                  )
                            }
                          />
                        ),
                      }}
                    />
                  </Text>
                </Row>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
})
